'use client';
/**
 * L'entrée « PARAMÈTRES » du référentiel des analyses (refonte du 18/08/2026).
 *
 * ── Pourquoi une entrée à part ──────────────────────────────────────────────
 *
 * La RQ : « je veux voir la liste des paramètres ; quand je clique sur un
 * paramètre, je vois ses détails ». Le paramètre est ce que le laboratoire
 * MESURE — l'examen est ce que le prescripteur COMMANDE. Les mélanger dans une
 * même liste obligeait à savoir, avant de chercher, de quel côté se trouvait ce
 * qu'on cherchait. Les trois entrées (Examens, Paramètres, Techniques) montrent
 * le MÊME référentiel sous trois angles, avec navigation croisée : depuis un
 * paramètre on saute à son examen ou à sa technique, sans changer de liste.
 *
 * Tout vient d'UNE lecture (`manuel-ref/parametres`, liaisons comprises) : le
 * détail s'ouvre sans second appel, donc sans état à moitié chargé.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { correspond, pertinence } from '@/lib/recherche-referentiel';
import { card, I, Skeleton, EmptyState, Pastille } from '@/components/ui';
import { useGed, userName } from '@/lib/store';
import { BoutonJournal } from '@/components/BoutonJournal';
import { trancheAgeLisible } from '@/shared/ageLisible';
import { Tableau } from '@/shared/Tableau';
import { marqueVersion } from '@/shared/versionsComposition';
import { REGLES_CODE, verifierCode, normaliserCode, motTronque } from '@/shared/codesReferentiel';
import { Modale, inputStyle, BoutonCreer, useConfirmation, Help } from '@/components/ui';
import { moteur, LiaisonsParametre } from './Registres';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { aujourdhuiIso, fmtDate } from '@/lib/meta';
/** Une option de liste, servie par le moteur avec son « i ». */
export type OptionVoc = { code: string; libelle: string; aide: string };
/** Une unité servie par le moteur, AVEC sa grandeur — c'est elle qui rend une
 *  conversion licite ou non. */
export type UniteVoc = { code: string; grandeur: string; aide: string };

export type ChampVoc = {
  cle: string; libelle: string; aide: string;
  nature: 'ferme' | 'suggere'; porte: 'parametre' | 'examen'; options: OptionVoc[];
};

/**
 * Une valeur de liste, affichée AVEC son explication.
 *
 * ⚠️ SANS LE « i », deux options voisines sont indiscernables à l'écran :
 * « Plasma » et « Plasma pauvre en plaquettes » se ressemblent, et l'un des
 * deux rend un temps de Quick faussement normal. C'est l'aide, et elle seule,
 * qui porte la différence au moment de choisir.
 *
 * ⚠️ UNE VALEUR HORS LISTE SE DIT. Elle s'affiche quand même — masquer une
 * donnée en base parce qu'elle ne figure plus au vocabulaire la ferait
 * disparaître sans que personne ne l'apprenne — mais elle est SIGNALÉE, pour
 * qu'on la reprenne.
 */
function valeurAvecAide(vocabulaire: ChampVoc[], cle: string, valeur: string | null) {
  if (!valeur) return null;
  const champ = vocabulaire.find((c) => c.cle === cle);
  const opt = champ?.options.find((o) => o.code === valeur);
  if (!opt) {
    return champ
      ? (
        <span title={`« ${valeur} » ne figure pas dans la liste des valeurs admises pour « ${champ.libelle} ».`}
          style={{ color: 'var(--status-warning)' }}>{valeur} ⚠️</span>
      )
      : <>{valeur}</>;
  }
  return <Help aide={opt.aide}>{opt.libelle}</Help>;
}

type Ref = { documentId: string; code: string; nom: string };
export type Parametre = {
  documentId: string; code: string; nom: string; version: number | null;
  discipline: string | null; matiere: string | null;
  uniteDefaut: string | null; uniteSecondaire: string | null;
  facteurConversion: number | null; conversionNote: string | null;
  /* La convention de décimales et la réserve de la société savante — RQ,
     24/08/2026. Elles se relisent ICI, là où la RQ arbitre, et pas seulement
     sur la fiche publique qu'elles alimentent. */
  decimales?: number | null; conversionRecommandee?: boolean | null;
  typeResultat: string | null; origineValeur: string | null;
  formeDiffusion: string | null; diffuseAuCatalogue: boolean;
  formule: string | null; alias: string | null;
  equipementCode: string | null; valeursUsuelles: string | null;
  /* Le prélèvement, en listes fermées depuis le 30/08/2026 (RQ) : la MATRICE
     est ce que l'automate mesure, le TUBE ce dans quoi elle est prélevée, la
     PRÉPARATION le geste qui fait passer de l'un à l'autre. */
  recipient?: string | null; preparation?: string | null;
  /* 07/09/2026 — la stabilité de la MATIÈRE SÉPARÉE, par paramètre (le tube
     primaire est régi par le profil de matériel). Source citée, endossée par
     la RQ ; changer une durée fait monter la version (champ définissant). */
  stabiliteAmbiante?: string | null; stabiliteRefrigeree?: string | null; stabiliteCongelee?: string | null;
  stabiliteSource?: string | null; stabiliteValidee?: boolean;
  archive: boolean;
  revueLe: string | null; revuePar: string | null; revueDecision: string | null;
  /* Repris de l'écran « Paramètres & valeurs usuelles », fermé le 30/08/2026 :
     il en était le seul porteur. */
  revueCommentaire?: string | null; revueProchaine?: string | null;
  /** d'où vient ce qui est affiché : le registre des revues, ou un tampon d'avant le 30/08 */
  revueSource?: 'registre' | 'tampon' | null;
  revueHistorique?: number;
  revueEnRetard?: boolean;
  /* ⚠️ JOINTS par le moteur depuis les registres qui les possèdent — rien
     n'est recopié sur le paramètre, rien ne peut donc diverger. La carte se
     rattache par une RELATION, le critère par le CODE : deux mécaniques, et
     c'est ainsi en base. */
  cartes?: { documentId: string; equipement: string; equipementNom: string; statut: string; actif: boolean }[];
  criteres?: { documentId: string; source: string; libelle: string; valeursUsuellesSource: string; unite: string }[];
  /** `role` : '' · 'principale' · 'secours' · 'verification' (01/09/2026). */
  techniques: (Ref & { actif: boolean; role?: string })[];
  composants: (Ref & { version: number | null })[];
  /* `techniques` sur l'examen porteur (26/08/2026) : c'est ce qui permet à
     l'arbitrage de PROPOSER les bonnes plutôt que d'offrir les 64 du
     laboratoire. Facultatif — un référentiel plus ancien ne le porte pas. */
  examens: { documentId: string; code: string; libelle: string; famille: string | null; version: number | null; techniques?: string[] }[];
  dvms: { ref: string; version: number | null; equipementCode: string | null; statut: string | null }[];
  intervalles: {
    documentId: string;
    sexe: string; ageMin: number | null; ageMax: number | null;
    grossesse: string; condition: string | null;
    /* Zone thérapeutique, seuil de décision, ou le cas ordinaire — cf. le
       schéma `intervalle-reference`. */
    nature?: string | null;
    borneMin: number | null; borneMax: number | null;
    unite: string | null; texteLibre: string | null;
    dateApplication: string | null; source: string | null;
    /** Non nul = la ligne est CLOSE : retirée de la fiche publique, conservée
     *  au registre, et rouvrable depuis l'écran (RQ, 19/08/2026). */
    dateFin: string | null;
    /** Non nul = le laboratoire répond de cette valeur, elle part au public. */
    adopteLe: string | null; adoptePar: string | null;
    commentaire: string | null;
  }[];
};

/** Un intervalle en une ligne lisible : « F · 150–350 µmol/L ».
 *
 *  Quand `texteLibre` est renseigné, il PORTE DÉJÀ la strate en toutes lettres
 *  (« Femme, Phase folliculaire : 30–120 pg/mL ») : y accoler le sexe et l'âge
 *  donnait « F · 0–130 ans · Femme, Phase folliculaire… », soit deux fois la
 *  même chose. On le rend alors tel quel. */
/** Le mot qui dit ce que la ligne EST — vide pour le cas ordinaire. */
export function motNature(nature: string | null | undefined): string {
  if (nature === 'zone-therapeutique') return 'Zone thérapeutique';
  if (nature === 'seuil-decision') return 'Seuil de décision';
  return '';
}

export function libelleIntervalle(i: Parametre['intervalles'][number]): string {
  /* LE MOT PASSE DEVANT, y compris sur un rendu en texte libre (RQ,
     24/08/2026). C'est justement là qu'il manquait le plus : la carbamazépine
     et l'acide valproïque n'ont PAS de bornes structurées, seulement une
     phrase — et cette phrase, rangée sous « valeurs de référence », se lisait
     comme une norme de population. */
  const mot = motNature(i.nature);
  if (i.texteLibre) return mot ? `${mot} · ${i.texteLibre}` : i.texteLibre;
  const qui = i.sexe === 'masculin' ? 'H' : i.sexe === 'feminin' ? 'F' : '';
  // L'âge dans l'unité où on le compte — jours, mois ou ans (RQ, 19/08/2026).
  // La RQ relit ici ce que le patient verra sur la fiche : les deux écrans
  // lisent la MÊME règle, dans le socle partagé.
  const age = trancheAgeLisible(i.ageMin, i.ageMax);
  const bornes = i.borneMin !== null && i.borneMax !== null ? `${i.borneMin} – ${i.borneMax}`
    : i.borneMax !== null ? `< ${i.borneMax}`
      : i.borneMin !== null ? `> ${i.borneMin}` : '—';
  return [mot, qui, age, i.condition, `${bornes}${i.unite ? ` ${i.unite}` : ''}`].filter(Boolean).join(' · ');
}

const FORMES: Record<string, string> = {
  composante: 'composante — se lit dans l’analyse qui le contient',
  analyse: 'analyse — commandable seul',
  lesdeux: 'les deux — seul ET dans les analyses composées',
};
const ORIGINES: Record<string, string> = {
  mesure: 'mesurée sur automate',
  calcule: 'calculée — ni automate, ni CQI, ni raccordement',
};

const cellule: React.CSSProperties = {
  padding: '7px 9px', borderBottom: '1px solid var(--border-subtle)',
  textAlign: 'left', verticalAlign: 'top', fontSize: 12.5,
};
const enTete: React.CSSProperties = {
  ...cellule, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.03em', color: 'var(--text-muted)', whiteSpace: 'nowrap',
};
const chip: React.CSSProperties = {
  display: 'inline-block', fontSize: 12.5, fontFamily: 'var(--font-mono)',
  border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '1px 8px',
  marginRight: 4, marginBottom: 3, background: 'var(--surface-card)',
};

/** Une rubrique du panneau : titre + lignes « libellé : valeur ». Les valeurs
 *  VIDES ne s'affichent pas — un panneau de tirets ne renseigne personne. */
/** Une ligne de rubrique : intitulé, valeur, et — s'il est modifiable — la clé
 *  du champ au catalogue partagé. */
export type LigneRubrique = [string, React.ReactNode] | [string, React.ReactNode, string];

/**
 * Un bloc du dossier.
 *
 * Les lignes VIDES sont masquées : un dossier criblé de « — » se lit moins bien
 * qu'un dossier court. Mais dès que la rubrique devient modifiable (`onEditer`),
 * les champs vides RÉAPPARAISSENT — sans quoi on ne pourrait jamais remplir ce
 * qui manque, ce qui est précisément ce qu'on vient chercher en modifiant.
 */
export function Rubrique({ titre, lignes, onEditer, champEnCours, editeur }: {
  titre: string; lignes: LigneRubrique[];
  onEditer?: (champ: string) => void;
  champEnCours?: string | null;
  /** L'éditeur À RENDRE DANS LA LIGNE du champ en cours (25/08/2026).
   *
   *  Il s'affichait en TÊTE du dossier : cliquer une ligne obligeait à remonter
   *  pour saisir, puis à redescendre pour vérifier — et sur un dossier de
   *  quarante lignes, on perdait de vue ce qu'on était en train de changer.
   *  Rendu ici, la saisie est à l'endroit exact de la valeur, et l'ANCIENNE
   *  reste lisible au-dessus, en grisé : on voit ce qu'on remplace. */
  editeur?: (champ: string) => React.ReactNode;
}) {
  const remplie = (v: React.ReactNode) => v !== null && v !== undefined && v !== '';
  const pleines = lignes.filter(([l, v, champ]) => remplie(v) || (onEditer && champ) || l === '');
  if (!pleines.length) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4 }}>{titre}</div>
      {pleines.map(([l, v, champ]) => {
        const modifiable = !!(onEditer && champ);
        const enEdition = !!(editeur && champ && champEnCours === champ);

        /* EN ÉDITION : la ligne devient un bloc de saisie. L'ancienne valeur
           reste affichée AU-DESSUS, en grisé et barrée — on remplace quelque
           chose, et le voir évite de retaper ce qui était déjà juste.
           ⚠️ Plus de `onClick` sur le conteneur dans cet état : il refermerait
           puis rouvrirait l'éditeur au moindre clic dans le champ de saisie. */
        if (enEdition) {
          return (
            <div key={l} style={{ padding: '6px 0', borderRadius: 'var(--radius-xs)', background: 'var(--cyan-50)' }}>
              <div style={{ display: 'flex', gap: 8, fontSize: 12.5 }}>
                <span style={{ color: 'var(--text-muted)', minWidth: 170, flexShrink: 0 }}>{l}</span>
                <span style={{ minWidth: 0, color: 'var(--text-faint)', textDecoration: remplie(v) ? 'line-through' : undefined }}>
                  {remplie(v) ? v : <span style={{ fontStyle: 'italic' }}>vide</span>}
                </span>
              </div>
              <div style={{ marginTop: 4 }}>{editeur(champ as string)}</div>
            </div>
          );
        }

        return (
          <div key={l} className={modifiable ? 'ged-ligne-modifiable' : undefined}
            style={{
              display: 'flex', gap: 8, fontSize: 12.5, padding: '1.5px 0',
              borderRadius: 'var(--radius-xs)', cursor: modifiable ? 'text' : undefined,
            }}
            /* Toute la ligne est la cible, pas une icône de 12 pixels : c'est
               la valeur qu'on veut changer, c'est donc la valeur qu'on clique. */
            onClick={modifiable ? () => onEditer(champ as string) : undefined}
            role={modifiable ? 'button' : undefined}
            tabIndex={modifiable ? 0 : undefined}
            onKeyDown={modifiable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEditer(champ as string); } } : undefined}
            title={modifiable ? 'Cliquer pour modifier' : undefined}>
            <span style={{ color: 'var(--text-muted)', minWidth: 170, flexShrink: 0 }}>{l}</span>
            <span style={{ minWidth: 0 }}>
              {remplie(v) ? v : <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>à renseigner</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}


/**
 * LES VALEURS USUELLES, à leur place : dans le paramètre.
 *
 * Elles ne se saisissaient que depuis l'EXAMEN (onglet « Intervalles » d'une
 * fiche), et la route de création exigeait un examen. C'était le mauvais
 * porteur : une borne d'hémoglobine appartient à l'hémoglobine, pas à la NFS
 * qui la contient — et la même borne recopiée sur la NFS, l'hémogramme et le
 * bilan d'anémie aurait divergé trois fois (RQ, 19/08/2026).
 *
 * Trois gestes, et trois seulement :
 *  · AJOUTER une valeur — elle naît adoptée, puisque c'est la RQ qui la saisit ;
 *  · ADOPTER une valeur reprise — le geste qui la fait passer au public ;
 *  · CLÔTURER — un intervalle ne se modifie jamais, il se clôt et un autre
 *    s'ouvre. Le résultat rendu hier a été interprété avec l'ancien.
 */
function ValeursUsuelles({ p, estRq, onEcrit, unites = [] }: {
  p: Parametre; estRq: boolean; onEcrit: () => void;
  /* ⚠️ Les unités viennent du MOTEUR, avec leur GRANDEUR. C'est ICI qu'elles se
     choisissent — le paramètre les LIT ensuite de ses valeurs usuelles (RQ,
     04/09/2026). Deux saisies pour un même fait finissent par diverger, et une
     borne exprimée dans une unité que le compte rendu n'utilise pas est un
     intervalle inapplicable. */
  unites?: UniteVoc[];
}) {
  const { settings } = useGed();
  const { demander, demanderTexte, confirmation } = useConfirmation();
  const [creation, setCreation] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [v, setV] = useState({
    sexe: 'tous', grossesse: 'indifferent', condition: '', nature: 'reference',
    ageMin: '', ageMax: '', borneMin: '', borneMax: '',
    unite: p.uniteDefaut || '', texteLibre: '', source: '', commentaire: '',
  });

  const agir = (chemin: string, corps: unknown, quoi: string) => {
    setEnvoi(true); setErreur('');
    void moteur(chemin, corps).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(`${quoi} : ${r.message}`); return; }
      setCreation(false); onEcrit();
    })
      // ⚠️ SANS ce `catch`, une requête qui échoue AVANT d'avoir une réponse —
      // moteur redémarré, réseau coupé, onglet en veille — laissait `envoi` à
      // `true` pour toujours : TOUS les boutons du panneau restaient désactivés,
      // sans un mot. C'est exactement le symptôme rapporté par la RQ le
      // 19/08/2026 — « les boutons d'adoption ne fonctionnent pas ». Le bouton
      // n'était pas cassé : il était grisé par un état jamais relâché.
      .catch(() => {
        setEnvoi(false);
        setErreur(`${quoi} : le serveur n’a pas répondu. Rien n’a été enregistré — réessayez.`);
      });
  };

  const creer = () => agir('manuel-ref/intervalles', {
    // PAS de ficheDocumentId : la valeur appartient au paramètre.
    analyteDocumentId: p.documentId,
    sexe: v.sexe, grossesse: v.grossesse,
    condition: v.condition.trim() || null,
    nature: v.nature,
    ageMin: v.ageMin.trim() === '' ? null : Number(v.ageMin),
    ageMax: v.ageMax.trim() === '' ? null : Number(v.ageMax),
    borneMin: v.borneMin.trim() === '' ? null : Number(v.borneMin),
    borneMax: v.borneMax.trim() === '' ? null : Number(v.borneMax),
    unite: v.unite.trim() || null,
    texteLibre: v.texteLibre.trim() || null,
    source: v.source.trim(),
    commentaire: v.commentaire.trim() || null,
    dateApplication: aujourdhuiIso(),
  }, 'Création');

  /* EN VIGUEUR d'un côté, ARCHIVÉES de l'autre.
   *
   * Le mot est celui de la RQ (19/08/2026) : « je préfère archiver, au lieu de
   * clôturer ». « Clôturer » ne disait pas ce que le geste fait — d'où un clic
   * de trop sur la 17-OHP le jour même. « Archiver » le dit : la valeur quitte
   * la fiche publique et rejoint le registre, elle n'est pas supprimée.
   *
   * ⚠️ Seul le MOT change. Le mécanisme reste `dateFin`, et la date garde tout
   * son sens : les résultats rendus avant elle ont été interprétés avec cette
   * borne-là. Un archivage sans date effacerait ce que le compte rendu d'hier
   * voulait dire. */
  const enVigueur = p.intervalles.filter((i) => !i.dateFin);
  const closes = p.intervalles.filter((i) => i.dateFin);

  const et: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-muted)' };
  const ch = (k: keyof typeof v, l: string, w = 90) => (
    <label style={et}>{l}<br />
      <input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.currentTarget.value })}
        style={{ ...inputStyle, width: w, fontSize: 12.5, height: 32 }} />
    </label>
  );

  return (
    <div style={{ marginBottom: 12 }}>
      {confirmation}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
          Valeurs usuelles
        </span>
        {estRq && !creation && (
          <BoutonCreer onClick={() => { setCreation(true); setErreur(''); }}>Ajouter une valeur</BoutonCreer>
        )}
      </div>

      {!enVigueur.length && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucune valeur usuelle en vigueur pour ce paramètre.
          {!!closes.length && ' Il en existe des archivées — voir ci-dessous.'}
        </div>
      )}

      {enVigueur.map((i) => (
        <div key={i.documentId}
          style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '3px 0', flexWrap: 'wrap' }}>
          <span style={{ flex: '1 1 240px', minWidth: 0 }} title={i.source || ''}>{libelleIntervalle(i)}</span>
          {i.adopteLe
            ? (
              <span style={{ fontSize: 12.5, color: 'var(--green-700)' }}
                title={`Adoptée le ${fmtDate(i.adopteLe)}${i.adoptePar ? ` par ${userName(settings, i.adoptePar)}` : ''}`}>
                ✓ publiée
              </span>
            )
            : (
              <span style={{ fontSize: 12.5, color: 'var(--amber-700)' }}
                title="Reprise non vérifiée : elle ne s’affiche pas sur la fiche publique.">
                non adoptée
              </span>
            )}
          {estRq && !i.adopteLe && (
            <Bouton variante="primaire" taille="sm" disabled={envoi} title="Le laboratoire prend cette valeur à son compte — elle passera sur la fiche publique et au livret." onClick={() => agir(`manuel-ref/intervalles/${i.documentId}/adopter`, {}, 'Adoption')}>
              Adopter
            </Bouton>
          )}
          {/* CLÔTURER retire la valeur de la fiche publique. Le geste était nu :
              un clic, et la borne disparaissait — c'est arrivé sur la 17-OHP le
              19/08/2026 (« c'était beaucoup trop facile »). Il porte désormais
              son effet dans son nom, et demande confirmation en RÉCITANT la
              ligne visée : sur un paramètre qui en compte huit, « êtes-vous
              sûr ? » ne dit pas laquelle on s'apprête à retirer. */}
          {/* DISCRET (28/10/2026) : un bouton plein sur chaque valeur publiée se
              lisait comme l'action principale de la ligne. Le geste reste là,
              confirmé, mais ne se propose plus en premier. */}
          {estRq && !i.dateFin && (
            <Bouton variante="discret" taille="sm" disabled={envoi} title="Retire cette valeur de la fiche publique et la range aux archives. Réversible : elle se restaure depuis cet écran." onClick={() => void (async () => {
                /* `useConfirmation` et non `window.confirm` (24/08/2026) — la
                   boîte de la maison, qui se ferme à Échap comme les autres. */
                if (!(await demander(
                  `Archiver cette valeur usuelle ?\n\n${libelleIntervalle(i)}\n\n`
                  + 'Elle quitte la fiche publique mais reste au registre, avec sa date : les résultats rendus avant '
                  + 'aujourd’hui ont été interprétés avec elle. Réversible depuis cet écran.',
                  { confirmer: 'Archiver' },
                ))) return;
                agir(`manuel-ref/intervalles/${i.documentId}/cloturer`,
                  { dateFin: aujourdhuiIso() }, 'Archivage');
              })()}>
              <I n="archive" s={12} />
              Archiver
            </Bouton>
          )}
          {/* LE CHEMIN DE RETOUR. Sans lui, la clôture était un aller simple :
              la donnée restait en base et l'écran ne savait pas la ramener, ce
              qui, pour qui l'utilise, revient exactement au même qu'une perte. */}
          {estRq && i.dateFin && (
            <Bouton taille="sm" disabled={envoi} title={`Archivée le ${fmtDate(i.dateFin)}. La remettre en vigueur — l’archivage et la restauration restent tous deux au journal.`} onClick={async () => {
                const motif = await demanderTexte(
                  `Remettre cette valeur en vigueur ?\n\n${libelleIntervalle(i)}\n\n`
                  + `Archivée le ${fmtDate(i.dateFin)}. L’archivage reste au journal, la restauration s’y ajoute.\n\nMotif :`,
                  { titre: 'Restaurer la valeur', confirmer: 'Restaurer', min: 5, valeurInitiale: 'Archivée par erreur' },
                );
                if (!motif) return;
                agir(`manuel-ref/intervalles/${i.documentId}/rouvrir`, { motif }, 'Restauration');
              }}>
              Restaurer
            </Bouton>
          )}
        </div>
      ))}

      {!!closes.length && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ fontSize: 12.5, color: 'var(--text-muted)', cursor: 'pointer' }}>
            {closes.length} valeur{closes.length > 1 ? 's' : ''} archivée{closes.length > 1 ? 's' : ''} — retirée{closes.length > 1 ? 's' : ''} de la fiche publique, conservée{closes.length > 1 ? 's' : ''} au registre
          </summary>
          {closes.map((i) => (
            <div key={i.documentId}
              style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '3px 0', flexWrap: 'wrap', opacity: 0.75 }}>
              <span style={{ flex: '1 1 240px', minWidth: 0 }} title={i.source || ''}>{libelleIntervalle(i)}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>archivée le {fmtDate(i.dateFin)}</span>
              {estRq && (
                <Bouton taille="sm" disabled={envoi} title="La remettre en vigueur — l’archivage et la restauration restent tous deux au journal." onClick={async () => {
                    const motif = await demanderTexte(
                      `Remettre cette valeur en vigueur ?\n\n${libelleIntervalle(i)}\n\n`
                      + `Archivée le ${fmtDate(i.dateFin)}. L’archivage reste au journal, la restauration s’y ajoute.\n\nMotif :`,
                      { titre: 'Restaurer la valeur', confirmer: 'Restaurer', min: 5, valeurInitiale: 'Archivée par erreur' },
                    );
                    if (!motif) return;
                    agir(`manuel-ref/intervalles/${i.documentId}/rouvrir`, { motif }, 'Restauration');
                  }}>
                  Restaurer
                </Bouton>
              )}
            </div>
          ))}
        </details>
      )}

      {erreur && <div role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5, marginTop: 6 }}>{erreur}</div>}

      {creation && (
        <div style={{ ...card, padding: 10, marginTop: 8, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={et}>Sexe<br />
              <select value={v.sexe} onChange={(e) => setV({ ...v, sexe: e.currentTarget.value })} style={{ fontSize: 12.5 }}>
                <option value="tous">Tous</option><option value="feminin">Féminin</option><option value="masculin">Masculin</option>
              </select>
            </label>
            <label style={et}>Grossesse<br />
              <select value={v.grossesse} onChange={(e) => setV({ ...v, grossesse: e.currentTarget.value })} style={{ fontSize: 12.5 }}>
                <option value="indifferent">Indifférent</option>
                <option value="hors-grossesse">Hors grossesse</option>
                <option value="grossesse">Grossesse</option>
                <option value="t1">1er trimestre</option><option value="t2">2e trimestre</option><option value="t3">3e trimestre</option>
              </select>
            </label>
            {ch('ageMin', 'Âge min')}
            {ch('ageMax', 'Âge max')}
            {ch('condition', 'Condition', 160)}
            {/* CE QUE LA LIGNE EST. Le choix est ici, à côté du sexe et de
                l'âge, parce qu'il décrit la ligne au même titre — et non dans
                les bornes, qu'il ne change pas. */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span style={{ color: 'var(--text-muted)' }}>Nature</span>
              <select value={v.nature} onChange={(e) => setV({ ...v, nature: e.target.value })}
                style={{ padding: '6px 8px', fontSize: 13, minWidth: 190 }}>
                <option value="reference">Intervalle de référence</option>
                <option value="zone-therapeutique">Zone thérapeutique (médicament)</option>
                <option value="seuil-decision">Seuil de décision clinique</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {ch('borneMin', 'Borne min')}
            {ch('borneMax', 'Borne max')}
            {/* ⚠️ CHOISIE, PAS TAPÉE. La saisie libre avait produit « G/L » face
                à « 10⁹/L » sur le même paramètre : deux graphies qu'aucune
                conversion ne rapproche, et un écran qui laisse croire à deux
                unités. */}
            <label style={et}>
              <Help aide="L’unité de cette borne. C’est ICI que l’unité du paramètre est décidée : il la lit de ses valeurs usuelles. Chaque unité porte sa grandeur, ce qui permet de vérifier qu’une conversion a un sens.">
                <span>Unité</span>
              </Help><br />
              <select value={v.unite} onChange={(e) => setV({ ...v, unite: e.target.value })}
                style={{ ...inputStyle, width: 150, fontSize: 12.5, height: 32 }}>
                <option value="">— aucune (rendu qualitatif) —</option>
                {unites.map((u) => <option key={u.code} value={u.code} title={u.aide}>{u.code}</option>)}
              </select>
            </label>
            {ch('texteLibre', 'Ou rendu qualitatif', 200)}
          </div>
          {ch('source', 'Source (obligatoire — un intervalle sans source ne se défend pas en audit)', 420)}
          <PiedActions taille="sm" annuler={{ onClick: () => setCreation(false), disabled: envoi }} principale={{ libelle: "Ajouter", disabled: envoi || !v.source.trim(), onClick: creer }} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Une valeur ajoutée ici est <strong>adoptée d’emblée</strong> — elle paraîtra sur la fiche
            publique et au livret dès l’enregistrement.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * LE BLOC UNITÉS ET PRÉCISION d'un paramètre, modifiable (RQ, 24/08/2026).
 *
 * ⚠️ Les six champs se règlent ENSEMBLE et non ligne à ligne, parce que deux
 * d'entre eux sont indissociables : une unité secondaire sans facteur de
 * conversion — ou l'inverse — n'affiche RIEN sur la fiche publique, et une
 * fenêtre qui n'affiche rien se lit comme une panne. Éditer champ par champ
 * aurait laissé enregistrer la moitié du couple.
 *
 * ⚠️ `decimales` est la convention d'AFFICHAGE du mesurande, jamais une
 * précision de stockage. La distinction s'est payée le 24/08 : le facteur du
 * cortisol, rangé dans un `decimal(10,2)`, y était tronqué de 0,03625 à 0,04.
 * On stocke tout, on arrondit à la lecture.
 */
function EditionUnites({ p, onFerme, onEcrit, unites = [] }: {
  p: Parametre; onFerme: () => void; onEcrit: () => void;
  /* Les unités, avec leur GRANDEUR : c'est elle qui dit si la conversion
     saisie est un changement d'échelle ou une conversion molaire. */
  unites?: UniteVoc[];
}) {
  const [v, setV] = useState({
    uniteDefaut: p.uniteDefaut || '',
    uniteSecondaire: p.uniteSecondaire || '',
    facteurConversion: p.facteurConversion !== null && p.facteurConversion !== undefined
      ? String(p.facteurConversion) : '',
    decimales: p.decimales !== null && p.decimales !== undefined ? String(p.decimales) : '',
    conversionRecommandee: p.conversionRecommandee !== false,
    conversionNote: p.conversionNote || '',
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const facteur = v.facteurConversion.trim().replace(',', '.');
  const nFacteur = facteur === '' ? null : Number(facteur);
  const seconde = v.uniteSecondaire.trim();
  /* On DIT le déséquilibre avant l'envoi : le moteur le refuse aussi, mais un
     refus arrivé après clic se lit comme une panne. */
  const couplePartiel = (!!seconde) !== (nFacteur !== null && Number.isFinite(nFacteur) && nFacteur > 0);
  const nDec = v.decimales.trim() === '' ? null : Number(v.decimales);
  const decHorsBornes = nDec !== null
    && (!Number.isInteger(nDec) || nDec < 0 || nDec > 6);

  /* L'APERÇU du calcul, dans le sens où il s'applique. C'est le piège de la
     créatinine : le facteur du texte archivé partait des mg/L alors que le
     laboratoire rend en µmol/L — l'appliquer tel quel multipliait par 78. Un
     facteur nu ne se relit pas ; écrit en toutes lettres, il se vérifie. */
  const apercu = !couplePartiel && seconde && nFacteur
    ? `${seconde} = ${v.uniteDefaut.trim() || 'unité de rendu'} × ${String(nFacteur).replace('.', ',')}`
    : null;

  /* ⚠️ LE VERDICT DE CONVERSION, RENDU AVANT LA SAISIE DU FACTEUR — c'est là
     qu'il sert. Le moteur refuse aussi les paires impossibles, mais un refus
     arrivé après clic se lit comme une panne ; et surtout, savoir que la
     conversion est MOLAIRE change ce qu'on va chercher : un facteur propre à
     cet analyte, jamais celui du voisin. */
  const verdict = (() => {
    const a = unites.find((u) => u.code === v.uniteDefaut.trim());
    const b = unites.find((u) => u.code === seconde);
    if (!a || !b || a.code === b.code) return null;
    if (a.grandeur === b.grandeur) {
      return { ton: 'ok' as const, texte: 'Changement d’échelle : le facteur est le même pour tous les analytes.' };
    }
    const masMol = (x: string, y: string) => (x === 'concentration_massique' && y === 'concentration_molaire')
      || (x === 'concentration_molaire' && y === 'concentration_massique');
    if (masMol(a.grandeur, b.grandeur)) {
      return {
        ton: 'attention' as const,
        texte: '⚠️ Conversion massique ↔ molaire : le facteur dépend de la MASSE MOLAIRE de cet analyte. '
          + 'Ne jamais reprendre celui d’un autre paramètre — c’est l’erreur qui avait multiplié la créatinine par 78.',
      };
    }
    return {
      ton: 'refus' as const,
      texte: `Aucune conversion possible entre ces deux grandeurs : ${a.code} et ${b.code} ne mesurent pas la même chose. `
        + 'Le moteur refusera d’enregistrer cette paire.',
    };
  })();

  return (
    <Modale titre="Unités et précision" sousTitre={`${p.code} — ${p.nom}`} onClose={onFerme}>
      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5 }}>
            <Help aide="L’unité que porte le compte rendu. Elle est normalement DÉDUITE des valeurs usuelles ; on ne la choisit ici que pour les paramètres qui n’en ont pas, ou dont les bornes existent en deux unités (glycémie : g/L et mmol/L).">
              <span>Unité de rendu</span>
            </Help><br />
            <select value={v.uniteDefaut} onChange={(e) => setV({ ...v, uniteDefaut: e.target.value })}
              style={{ ...inputStyle, width: 160 }}>
              <option value="">— aucune —</option>
              {unites.map((u) => <option key={u.code} value={u.code} title={u.aide}>{u.code}</option>)}
            </select></label>
          <label style={{ fontSize: 12.5 }}>Décimales affichées<br />
            <input type="number" min={0} max={6} value={v.decimales} placeholder="auto"
              onChange={(e) => setV({ ...v, decimales: e.currentTarget.value })}
              style={{ ...inputStyle, width: 100 }} />
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, maxWidth: 220 }}>
              Vide = trois chiffres significatifs.
            </div></label>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginTop: 4 }}>
          Seconde unité — facultative
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <label style={{ fontSize: 12.5 }}>
            <Help aide="L’unité de conversion affichée à titre indicatif à côté du résultat. Elle forme une PAIRE ORDONNÉE avec l’unité de rendu : c’est cette paire que le facteur convertit.">
              <span>Unité secondaire</span>
            </Help><br />
            <select value={v.uniteSecondaire} onChange={(e) => setV({ ...v, uniteSecondaire: e.target.value })}
              style={{ ...inputStyle, width: 160 }}>
              <option value="">— aucune —</option>
              {unites.map((u) => <option key={u.code} value={u.code} title={u.aide}>{u.code}</option>)}
            </select></label>
          <label style={{ fontSize: 12.5 }}>Facteur de conversion<br />
            <input value={v.facteurConversion} onChange={(e) => setV({ ...v, facteurConversion: e.currentTarget.value })}
              style={{ ...inputStyle, width: 150, fontFamily: 'var(--font-mono)' }} /></label>
        </div>
        {verdict && (
          <div style={{
            fontSize: 12.5, lineHeight: 1.55, padding: '8px 11px', borderRadius: 'var(--radius-sm)',
            background: verdict.ton === 'refus' ? 'var(--red-50)'
              : verdict.ton === 'attention' ? 'var(--amber-50)' : 'var(--surface-sunken)',
            border: `1px solid ${verdict.ton === 'refus' ? 'var(--red-600)'
              : verdict.ton === 'attention' ? 'var(--amber-300)' : 'var(--border-default)'}`,
          }}>{verdict.texte}</div>
        )}
        {apercu && (
          <div style={{ fontSize: 12.5, padding: '7px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
            Calcul appliqué : <code style={{ fontSize: 12.5 }}>{apercu}</code>
          </div>
        )}
        {couplePartiel && (
          <div style={{ fontSize: 12.5, color: 'var(--amber-700)' }}>
            L’unité secondaire et le facteur vont ensemble : l’un sans l’autre n’affiche rien.
            Posez les deux, ou laissez les deux vides.
          </div>
        )}
        <label style={{ fontSize: 12.5, display: 'flex', gap: 7, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={v.conversionRecommandee}
            onChange={(e) => setV({ ...v, conversionRecommandee: e.currentTarget.checked })} />
          <span>Recommandée par la société savante
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2, maxWidth: 420 }}>
              Décochez si la société savante déconseille la conversion. La valeur convertie
              reste affichée — le calcul est juste — mais la réserve paraît à côté.
            </div>
          </span>
        </label>
        <label style={{ fontSize: 12.5 }}>Formule, explication et référence<br />
          <textarea value={v.conversionNote} rows={4}
            onChange={(e) => setV({ ...v, conversionNote: e.currentTarget.value })}
            style={{ ...inputStyle, width: '100%', resize: 'vertical' }} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
            Masse molaire, calcul, société savante. Ce texte paraît sur la fiche publique,
            le livret imprimé et ici.
          </div></label>

        {erreur && <div style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
        <PiedActions taille="sm" annuler={{ onClick: onFerme }} principale={{ libelle: "Enregistrer", disabled: envoi || couplePartiel || decHorsBornes, onClick: () => {
              setEnvoi(true); setErreur('');
              void moteur(`manuel-ref/parametres/${p.documentId}/unites`, {
                uniteDefaut: v.uniteDefaut.trim() || null,
                uniteSecondaire: seconde || null,
                facteurConversion: nFacteur,
                decimales: nDec,
                conversionRecommandee: v.conversionRecommandee,
                conversionNote: v.conversionNote.trim() || null,
              }).then((r) => {
                setEnvoi(false);
                if (!r.ok) { setErreur(r.message); return; }
                onEcrit(); onFerme();
              })
                /* ⚠️ SANS ce `catch`, une requête qui échoue AVANT d'avoir une
                   réponse laisse `envoi` à `true` pour toujours : le bouton
                   reste grisé, sans un mot. C'est le défaut exact rapporté par
                   la RQ le 19/08 sur les boutons d'adoption. */
                .catch(() => {
                  setEnvoi(false);
                  setErreur('Le serveur n’a pas répondu. Rien n’a été enregistré — réessayez.');
                });
            } }} />
      </div>
    </Modale>
  );
}

/**
 * L'ARBITRAGE « QUELLE TECHNIQUE POUR CE PARAMÈTRE » (RQ, 26/08/2026).
 *
 * ── Pourquoi cet écran existe ───────────────────────────────────────────────
 *
 * La technique relève du paramètre, pas de l'examen : on mesure un mesurande.
 * La reprise automatique traite les 210 examens à UN SEUL paramètre, où le
 * rattachement est mécanique. Restent 47 examens à plusieurs paramètres, où
 * « quelle technique pour lequel » est un jugement de biologiste — un examen
 * qui mêle colorimétrie et PCR ne se répartit pas tout seul.
 *
 * C'est ici que cela se tranche : dans le dossier du PARAMÈTRE, à l'endroit où
 * la décision s'applique, et non dans un écran de reprise que l'on quitte sans
 * savoir ce qu'on a fait.
 *
 * ⚠️ LES TECHNIQUES PROPOSÉES SONT CELLES DE SES EXAMENS. Offrir les 64 du
 * laboratoire demanderait de retrouver de mémoire lesquelles concernent ce
 * paramètre — et c'est précisément ce qu'on cherche à ne plus faire faire. La
 * liste complète reste accessible d'un bouton, pour le cas où la bonne réponse
 * n'est pas dans les examens (une technique retirée de la fiche par erreur).
 */
/**
 * Les trois rôles d'une technique POUR UN PARAMÈTRE DONNÉ.
 *
 * ⚠️ « Secours » et « vérification » ne sont pas deux mots pour la même chose :
 * l'une remplace quand l'automate est en panne, l'autre confirme un résultat
 * douteux. Les confondre ferait rendre un résultat de contrôle comme un
 * résultat de patient.
 */
const ROLE_TECHNIQUE: Record<string, string> = {
  principale: 'par défaut',
  secours: 'en secours',
  verification: 'en vérification',
};

function TechniquesDuParametre({ p, estRq, onEcrit, ouvrirTechnique }: {
  p: Parametre; estRq: boolean; onEcrit?: () => void; ouvrirTechnique: (code: string) => void;
}) {
  const [edite, setEdite] = useState(false);
  const [choix, setChoix] = useState<string[]>([]);
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [toutes, setToutes] = useState<{ code: string; nom: string }[] | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const ouvrir = () => {
    setChoix(p.techniques.map((t) => t.code));
    setRoles(Object.fromEntries(p.techniques.filter((t) => t.role).map((t) => [t.code, t.role as string])));
    setErreur('');
    setEdite(true);
    if (!toutes) {
      void moteur('manuel-ref/techniques').then((r) => {
        if (r.ok) setToutes(((r.data.techniques as { code: string; nom: string }[]) || []));
      });
    }
  };

  /* Les techniques des examens qui portent ce paramètre : la réponse est
     presque toujours là. `suggerees` reste un SOUS-ENSEMBLE proposé — jamais
     appliqué d'office : rattacher toutes les techniques de l'examen à tous ses
     paramètres est exactement l'affirmation fausse que cet écran évite. */
  const suggerees = [...new Set(p.examens.flatMap((x) => x.techniques || []))];
  const liste = toutes || suggerees.map((c) => ({ code: c, nom: c }));

  const basculer = (code: string) =>
    setChoix((l) => (l.includes(code) ? l.filter((x) => x !== code) : [...l, code]));

  const enregistrer = () => {
    setEnvoi(true); setErreur('');
    void moteur(`manuel-ref/parametres/${p.documentId}/techniques`, { techniques: choix, roles }).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      setEdite(false);
      onEcrit?.();
    });
  };

  if (!edite) {
    return (
      /* ⚠️ UNE LIGNE PAR TECHNIQUE, plus des pastilles côte à côte (RQ,
         01/09/2026 : « il y a une technique ECLIA ELFA — non, ce sont deux
         techniques là »). Elles l'ont toujours été en base : vingt paramètres
         portent les deux, et c'est le rendu en ligne, séparé d'un simple
         espace, qui les faisait lire comme une seule. Empiler règle le fond,
         et laisse la place au rôle — lequel des deux sert par défaut. */
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'flex-start', flexDirection: 'column' }}>
        {p.techniques.length ? p.techniques.map((t) => (
          <span key={t.documentId} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <button className="ged-btn" style={{ ...chip, cursor: 'pointer', opacity: t.actif ? 1 : 0.55 }}
              title={t.actif ? t.nom : `${t.nom} — inactive`} onClick={() => ouvrirTechnique(t.code)}>{t.code}</button>
            {t.role && (
              <span style={{
                fontSize: 12.5, fontWeight: t.role === 'principale' ? 700 : 400,
                color: t.role === 'principale' ? 'var(--green-600)' : 'var(--text-muted)',
              }}>{ROLE_TECHNIQUE[t.role] || t.role}</span>
            )}
          </span>
        )) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>
            aucune — la technique est encore portée par l’examen
          </span>
        )}
        {estRq && (
          <Bouton taille="sm" onClick={ouvrir} title="Déclarer les techniques qui mesurent ce paramètre">
            <I n="pen" s={12} />
          </Bouton>
        )}
      </span>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {!!suggerees.length && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Proposées d’après {p.examens.length > 1 ? 'les examens qui le portent' : 'l’examen qui le porte'}.
          Cochez celles qui mesurent <strong>ce paramètre</strong> — pas toutes celles de l’examen.
        </div>
      )}
      <div style={{ display: 'grid', gap: 2, maxHeight: 220, overflowY: 'auto' }}>
        {liste.map((t) => (
          <label key={t.code} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, padding: '3px 2px', cursor: 'pointer' }}>
            <input type="checkbox" checked={choix.includes(t.code)} onChange={() => basculer(t.code)} />
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{t.code}</span>
            <span style={{ color: 'var(--text-muted)', flex: 1, minWidth: 0 }}>{t.nom !== t.code ? t.nom : ''}</span>
            {suggerees.includes(t.code) && (
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cyan-700)' }}>proposée</span>
            )}
            {/* Le rôle ne s'offre QUE sur une technique cochée : le proposer
                ailleurs laisserait poser une décision sur un lien qui n'existe
                pas, et le serveur l'écarterait sans le dire. */}
            {choix.includes(t.code) && (
              <select value={roles[t.code] || ''} style={{ ...inputStyle, width: 155, padding: '2px 6px' }}
                onClick={(e) => e.preventDefault()}
                onChange={(e) => setRoles((r) => {
                  const suivant = { ...r };
                  if (e.target.value) suivant[t.code] = e.target.value; else delete suivant[t.code];
                  return suivant;
                })}>
                <option value="">rôle non précisé</option>
                <option value="principale">utilisée par défaut</option>
                <option value="secours">en secours</option>
                <option value="verification">en vérification</option>
              </select>
            )}
          </label>
        ))}
        {/* La règle est portée par le serveur ; la dire ici évite d'aller
            chercher le refus après coup. */}
        {Object.values(roles).filter((r) => r === 'principale').length > 1 && (
          <div role="alert" style={{ color: 'var(--amber-600)', fontSize: 12.5, lineHeight: 1.5 }}>
            Une seule technique peut être <strong>utilisée par défaut</strong> : c’est celle qui rend le
            résultat quand tout va bien. Les autres se déclarent en secours ou en vérification.
          </div>
        )}
      </div>
      {erreur && <div role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        La liste remplace : ce qui n’est pas coché ne sera plus rattaché à ce paramètre.
        Le lien de l’examen, lui, n’est pas touché.
      </div>
      <PiedActions taille="sm" annuler={{ onClick: () => setEdite(false), disabled: envoi }} principale={{ libelle: "Enregistrer", disabled: envoi, onClick: enregistrer }} />
    </div>
  );
}

/**
 * DÉCLARER LA REVUE DES VALEURS USUELLES.
 *
 * ⚠️ Repris tel quel de l'écran « Paramètres & valeurs usuelles », fermé le
 * 30/08/2026 : il en était le seul porteur.
 *
 * ⚠️ CE N'EST PAS UNE CASE À COCHER. La décision est OBLIGATOIRE, et les deux
 * réponses ne veulent pas dire la même chose : « j'ai regardé et je maintiens »
 * se prouve, « j'ai regardé et j'ai changé » appelle une modification derrière.
 * Un bouton unique « revue faite » aurait confondu les deux.
 *
 * ⚠️ ET ELLE NE MODIFIE PAS LES BORNES. Celles-ci vivent dans leur registre
 * avec leur version et leur date d'application ; la revue atteste qu'on les a
 * REGARDÉES. Les fondre ferait qu'une revue sans changement ne laisserait
 * aucune trace — c'est-à-dire le cas le plus fréquent, et celui qu'il faut
 * pouvoir prouver.
 */
function DeclarerRevue({ p, onFerme, onEcrit }: {
  p: Parametre; onFerme: () => void; onEcrit: () => void;
}) {
  const [decision, setDecision] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  return (
    <Modale titre="Déclarer la revue des valeurs usuelles"
      sousTitre={`${p.code} — ${p.nom}`} onClose={onFerme} largeur={520}
      pied={(
        <>
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton variante="primaire" disabled={!decision || enCours} onClick={() => {
              setEnCours(true);
              setErreur('');
              /* ⚠️ LE REGISTRE, ET LUI SEUL (30/08/2026). Ce bouton écrivait
                 dans `ged-analyte.revue*` — un TAMPON porté par le paramètre —
                 pendant que le centre de revue écrivait dans `ged-revue-ref`.
                 Deux registres du même fait : la RQ a revu la 17OHP au centre
                 et la fiche a continué d'afficher « jamais revue ».

                 On envoie donc à la MÊME route que le centre. Elle refuse une
                 revue sur un paramètre sans intervalle adopté, exige de dire ce
                 qui doit changer quand la conclusion est « à modifier », fige
                 l'instantané de ce qui a été relu et signe le journal — quatre
                 garanties que ce formulaire n'avait pas.

                 `prochaine` n'est plus envoyée : l'échéance est CALCULÉE depuis
                 la périodicité de la discipline. Une date figée ici
                 continuerait d'annoncer l'ancien rythme après un changement de
                 paramétrage. */
              void moteur('revues-ref', {
                parametres: [p.code],
                conclusion: decision === 'maintenues' ? 'inchange' : 'a_modifier',
                commentaire: commentaire.trim(),
              })
                .then((r) => {
                  setEnCours(false);
                  if (r.ok) { onEcrit(); onFerme(); } else {
                    setErreur(r.message || 'La revue n’a pas été enregistrée.');
                  }
                });
            }}>
            {enCours ? 'Enregistrement…' : 'Enregistrer la revue'}
          </Bouton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>
          La revue atteste que les valeurs usuelles ont été <strong>regardées</strong>. Elle ne
          les modifie pas : les bornes se tiennent au référentiel, où elles portent l’âge, le
          sexe, la grossesse et leur version.
        </p>
        <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
          Décision
          <select style={inputStyle} value={decision} onChange={(e) => setDecision(e.target.value)}>
            <option value="">— à choisir —</option>
            <option value="maintenues">Valeurs maintenues en l’état</option>
            <option value="modifiees">Valeurs modifiées (les modifier ensuite ci-dessus)</option>
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
          Commentaire
          <textarea style={{ ...inputStyle, minHeight: 70 }} value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            placeholder="Ce qui a été confronté : population, source, littérature…" />
        </label>
        {/* ⚠️ LE CHAMP « PROCHAINE REVUE » A ÉTÉ RETIRÉ LE 30/08/2026. Il
            faisait SAISIR une échéance que le laboratoire a déjà fixée : la
            périodicité de revue se règle par discipline, au centre de revue, et
            l'échéance s'en déduit. Une date tapée ici l'aurait emporté sur la
            règle, et serait restée juste jusqu'au jour où la périodicité
            change — après quoi elle aurait annoncé un rythme abandonné, sans
            que rien ne le signale. */}
        {/* ⚠️ Dire que l'acte est NOMINATIF avant de le poser : c'est ce qui
            distingue une revue d'une case cochée en passant. */}
        {/* ⚠️ Un refus se DIT. Une fenêtre qui se referme sans rien enregistrer
            est pire qu'un message d'erreur : on croit avoir déclaré la revue. */}
        {erreur && (
          <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--status-danger)' }}>{erreur}</p>
        )}
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
          La revue est datée, signée de votre nom et écrite au journal sous le code du paramètre.
        </p>
      </div>
    </Modale>
  );
}

/**
 * LES STABILITÉS DU PARAMÈTRE (07/09/2026). Trois durées sur la matière
 * séparée, la source, et l'endossement. Une durée changée retire
 * l'endossement (ce qui a changé n'a pas été relu) et fait monter la version
 * du paramètre — donc celle des examens qui le portent (champ définissant).
 */
function EditionStabilites({ p, onFerme, onEcrit }: { p: Parametre; onFerme: () => void; onEcrit: () => void }) {
  const [f, setF] = useState({
    stabiliteAmbiante: p.stabiliteAmbiante || '', stabiliteRefrigeree: p.stabiliteRefrigeree || '',
    stabiliteCongelee: p.stabiliteCongelee || '', stabiliteSource: p.stabiliteSource || '',
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const champ = (cle: keyof typeof f, libelle: string, aide: string) => (
    <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
      <Help aide={aide}><span style={{ fontWeight: 600 }}>{libelle}</span></Help>
      <input value={f[cle]} onChange={(e) => setF({ ...f, [cle]: e.currentTarget.value })} style={inputStyle} maxLength={255} />
    </label>
  );
  const envoyer = (valider: boolean) => {
    setEnvoi(true); setErreur('');
    void moteur(`manuel-ref/parametres/${p.documentId}/stabilites`, { ...f, stabiliteValidee: valider })
      .then((r) => { setEnvoi(false); if (r.ok) { onEcrit(); onFerme(); } else setErreur(r.message || 'L’enregistrement a échoué.'); });
  };
  return (
    <Modale titre="Stabilités du paramètre" sousTitre={`${p.code} — ${p.nom}`} onClose={onFerme} largeur={560}
      pied={(
        <>
          {erreur && <span style={{ fontSize: 12.5, color: 'var(--red-600)', alignSelf: 'center' }}>{erreur}</span>}
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton taille="sm" disabled={envoi} onClick={() => envoyer(false)}>Enregistrer (à relire)</Bouton>
          <Bouton variante="primaire" disabled={envoi} onClick={() => envoyer(true)}>Enregistrer et endosser</Bouton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>
          Sur la <strong>matière séparée</strong> (sérum, plasma décanté…). Le tube primaire, lui, suit son
          profil de matériel. « Interdit », « Sans objet (calculé) » sont des réponses : une case vide
          se lit comme un oubli.
        </p>
        {champ('stabiliteAmbiante', 'Température ambiante', 'Durée à 18–25 °C après séparation.')}
        {champ('stabiliteRefrigeree', 'Réfrigérée 2–8 °C', 'Durée au réfrigérateur après séparation.')}
        {champ('stabiliteCongelee', 'Congelée', 'Durée à −20 °C (préciser −80 °C si différent) ; un seul cycle gel-dégel sauf mention.')}
        {champ('stabiliteSource', 'Source', 'D’où vient la valeur : SFBC, GEHT, notice fournisseur, vérification interne…')}
      </div>
    </Modale>
  );
}

/** Une liste du prélèvement, avec l'aide du champ et celle de l'option choisie.
 *  Hors d'`EditionPrelevement` : un composant déclaré dans un autre est recréé
 *  à chaque rendu, et la liste perd alors son focus. */
function ListePrelevement({ cle, c, valeur, poser, videLabel }: {
  cle: string; c: ChampVoc | undefined; valeur: string; poser: (v: string) => void; videLabel: string;
}) {
  const opt = c?.options.find((o) => o.code === valeur);
  return (
    <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
      {/* Le « i » du CHAMP, au-dessus de la liste : on le lit AVANT de
          choisir, pas après s'être trompé. */}
      <Help aide={c?.aide || ''}>
        <span style={{ fontWeight: 600 }}>{c?.libelle || cle}</span>
      </Help>
      <select style={inputStyle} value={valeur} onChange={(e) => poser(e.target.value)}>
        <option value="">{videLabel}</option>
        {(c?.options || []).map((o) => <option key={o.code} value={o.code}>{o.libelle}</option>)}
      </select>
      {/* Le « i » de l'OPTION choisie, sous la liste : il change à chaque
          sélection, et c'est lui qui distingue deux voisines. */}
      <span style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-muted)', minHeight: 32 }}>
        {opt?.aide || ''}
      </span>
    </label>
  );
}

/**
 * MODIFIER LE PRÉLÈVEMENT — trois listes, aucune prose.
 *
 * ⚠️ CHAQUE LISTE VIENT DU MOTEUR, avec l'explication de chaque option. Une
 * liste recopiée ici divergerait de celle que la route accepte : l'écran
 * proposerait un choix refusé au moment d'enregistrer, sans qu'on comprenne
 * pourquoi.
 *
 * ⚠️ « — aucune — » EST UNE OPTION, et elle est expliquée : un paramètre
 * calculé n'a pas de matrice, et l'absence est alors une réponse. La confondre
 * avec un oubli ferait réclamer un tube pour un DFG.
 */
function EditionPrelevement({ p, vocabulaire, onFerme, onEcrit }: {
  p: Parametre; vocabulaire: ChampVoc[]; onFerme: () => void; onEcrit: () => void;
}) {
  const [matiere, setMatiere] = useState(p.matiere || '');
  const [recipient, setRecipient] = useState(p.recipient || '');
  const [preparation, setPreparation] = useState(p.preparation || '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const champ = (cle: string) => vocabulaire.find((c) => c.cle === cle);
  const calcule = p.origineValeur === 'calcule';


  return (
    <Modale titre="Prélèvement du paramètre" sousTitre={`${p.code} — ${p.nom}`}
      onClose={onFerme} largeur={560}
      pied={(
        <>
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton variante="primaire" disabled={envoi} onClick={() => {
              setEnvoi(true); setErreur('');
              void moteur(`manuel-ref/parametres/${p.documentId}/prelevement`,
                { matiere: matiere || null, recipient: recipient || null, preparation: preparation || null })
                .then((r) => {
                  setEnvoi(false);
                  if (r.ok) { onEcrit(); onFerme(); } else {
                    setErreur(r.message || 'L’enregistrement a échoué.');
                  }
                });
            }}>{envoi ? 'Enregistrement…' : 'Enregistrer'}</Bouton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>
          La <strong>matrice</strong> est ce que l’automate mesure ; le <strong>milieu</strong> — ce
          qu’on prélève au patient — appartient à l’examen. Un même tube EDTA donne du sang total
          pour la numération et du plasma pour l’ACTH.
        </p>
        {calcule && (
          /* ⚠️ Dire POURQUOI les trois cases sont vides sur un calculé : sans
             cette phrase, on les remplit « pour faire propre », et le DFG se met
             à réclamer un tube. */
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, padding: '9px 12px',
            background: 'var(--amber-50)', border: '1px solid var(--amber-300)',
            borderRadius: 'var(--radius-md)' }}>
            Ce paramètre est <strong>calculé</strong> : il n’est mesuré sur rien et ne demande aucun
            tube. Laisser la matrice vide est la bonne réponse — ce sont ses composants qui portent
            le prélèvement.
          </p>
        )}
        <ListePrelevement cle="matiere" c={champ('matiere')} valeur={matiere} poser={setMatiere}
          videLabel="— aucune : paramètre calculé —" />
        <ListePrelevement cle="preparation" c={champ('preparation')} valeur={preparation} poser={setPreparation}
          videLabel="— à trancher —" />
        <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
          <Help aide="Le tube dans lequel la matrice est prélevée. ⚠️ Il ne se déduit PAS de la matrice : un plasma sort d’un tube EDTA, fluoré ou hépariné selon l’analyse. L’examen dresse la liste de ses tubes à partir de ses paramètres — il ne la saisit pas.">
            <span style={{ fontWeight: 600 }}>Tube</span>
          </Help>
          <select style={inputStyle} value={recipient} onChange={(e) => setRecipient(e.target.value)}>
            <option value="">— aucun —</option>
            {['gel', 'sec', 'edta', 'citrate', 'heparine', 'fluorure', 'hemoc', 'seringue',
              'urine', 'selles', 'ecouvillon', 'sterile', 'expecto', 'lcr', 'biopsie', 'cytobrosse']
              .map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        {erreur && <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</p>}
      </div>
    </Modale>
  );
}

export function PanneauParametre({ p, onFerme, ouvrirExamen, ouvrirTechnique, estRq = false, onEcrit, vocabulaire = [], unites = [], enPanneau = false }: {
  p: Parametre; onFerme: () => void;
  /** EN PANNEAU (18/10/2026) : le même dossier, rendu dans le panneau des fiches
   *  liées ouvert depuis un examen — sans fenêtre, le panneau en est déjà une.
   *  Un seul dossier par paramètre, quel que soit l'endroit d'où on l'ouvre. */
  enPanneau?: boolean;
  ouvrirExamen: (code: string) => void; ouvrirTechnique: (code: string) => void;
  estRq?: boolean; onEcrit?: () => void;
  /* ⚠️ Les listes et leurs « i » viennent du MOTEUR, jamais d'une copie ici.
     Deux définitions du même vocabulaire divergent à la première option
     ajoutée — et l'écran proposerait alors un choix que la route refuse. */
  vocabulaire?: ChampVoc[];
  unites?: UniteVoc[];
}) {
  /* Le bloc unités s'ouvre en fenêtre, pas en édition de ligne : ses six
     champs se valident ensemble (cf. `EditionUnites`). */
  const [modifUnites, setModifUnites] = useState(false);
  const [revue, setRevue] = useState(false);
  const [prelevement, setPrelevement] = useState(false);
  const [stabilites, setStabilites] = useState(false);
  /* Les liaisons d'un paramètre CALCULÉ (formule, composants) — l'éditeur
     vivait sur la fiche en panneau, retirée le 18/10/2026 ; il vit ici. */
  const [editionCalcul, setEditionCalcul] = useState(false);
  const sousTitre = (<>
    {marqueVersion(p.version)}
    {/* Le journal du paramètre : sa clé est son CODE. */}
    <span style={{ marginLeft: 8 }}><BoutonJournal reference={p.code} quoi="de ce paramètre" /></span>
    {p.archive && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-md)', padding: '2px 9px', background: 'var(--gray-100)', color: 'var(--gray-700)' }}>archivé</span>}
  </>);
  const corps = (
    /* ── EN FENÊTRE POPUP, PAS EN HAUT DE LA TABLE (RQ, 05/09/2026) ──────────
       La fiche s'ouvrait en bloc inline au-dessus du tableau, qui poussait le
       reste de la liste vers le bas : on perdait la ligne cliquée hors écran
       en l'ouvrant. `Modale` est le même composant que le dossier personnel et
       la fiche de fonction — sansSaisie, la croix ferme sans redemander, cette
       fiche ne fait QUE montrer (les sous-écrans d'édition restent leurs
       propres fenêtres, imbriquées comme aujourd'hui). */
    <>
      {modifUnites && (
        <EditionUnites p={p} onFerme={() => setModifUnites(false)}
          onEcrit={() => onEcrit?.()} unites={unites} />
      )}
      {revue && (
        <DeclarerRevue p={p} onFerme={() => setRevue(false)} onEcrit={() => onEcrit?.()} />
      )}
      {prelevement && (
        <EditionPrelevement p={p} vocabulaire={vocabulaire}
          onFerme={() => setPrelevement(false)} onEcrit={() => onEcrit?.()} />
      )}
      {stabilites && (
        <EditionStabilites p={p} onFerme={() => setStabilites(false)} onEcrit={() => onEcrit?.()} />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '0 24px' }}>
        <div>
          <Rubrique titre="Identité" lignes={[
            ['Discipline', p.discipline],
            ['Matière', p.matiere],
            ['Synonymes', p.alias],
            ['Type de résultat', p.typeResultat],
            ['Origine de la valeur', p.origineValeur ? (ORIGINES[p.origineValeur] || p.origineValeur) : null],
            ['Formule', p.formule ? <code style={{ fontSize: 12.5 }}>{p.formule}</code> : null],
          ]} />
          {/* Le bouton est DANS le flux, au-dessus de la rubrique qu'il ouvre :
              un réglage qui n'a de bouton nulle part se règle en base, et c'est
              exactement ce qui se passait jusqu'au 24/08/2026 — aucune route
              ne modifiait un paramètre existant. */}
          {estRq && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -18 }}>
              <Bouton taille="sm" onClick={() => setModifUnites(true)}>Modifier les unités</Bouton>
            </div>
          )}
          <Rubrique titre="Unités" lignes={[
            ['Unité par défaut', p.uniteDefaut],
            ['Unité secondaire', p.uniteSecondaire],
            ['Facteur de conversion', p.facteurConversion !== null ? String(p.facteurConversion).replace('.', ',') : null],
            /* LA FORMULE ÉCRITE EN TOUTES LETTRES. La RQ relit un facteur nu
               sans pouvoir dire dans quel SENS il s'applique — c'est
               exactement le piège de la créatinine, dont le facteur du texte
               archivé partait des mg/L alors que le laboratoire rend en
               µmol/L : l'appliquer tel quel multipliait les valeurs par 78. */
            ['Calcul appliqué', p.facteurConversion !== null && p.uniteSecondaire
              ? <code style={{ fontSize: 12.5 }}>
                  {`${p.uniteSecondaire} = ${p.uniteDefaut || 'unité de rendu'} × ${String(p.facteurConversion).replace('.', ',')}`}
                </code>
              : null],
            ['Décimales affichées', p.decimales != null ? String(p.decimales) : null],
            ['Recommandée par la société savante',
              p.facteurConversion === null ? null
                : p.conversionRecommandee === false
                  ? <span style={{ color: 'var(--status-warning)' }}>
                      non — valeur convertie donnée à titre indicatif
                    </span>
                  : 'oui'],
            ['Note de conversion', p.conversionNote],
            ['Valeurs usuelles', p.valeursUsuelles],
          ]} />
          {/* ══════════════════════════════════════════════════════════════
              LE PRÉLÈVEMENT — trois listes fermées là où il y avait de la prose
              (RQ, 30/08 puis 04/09/2026). Chaque ligne porte son « i » : sans
              lui, « Plasma » et « Plasma pauvre en plaquettes » ne se
              distinguent par RIEN à l'écran, alors que l'un rend un TP faux.
              ══════════════════════════════════════════════════════════════ */}
          {estRq && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -18 }}>
              <Bouton taille="sm" onClick={() => setPrelevement(true)}>Modifier le prélèvement</Bouton>
            </div>
          )}
          <Rubrique titre="Prélèvement" lignes={[
            ['Matrice', valeurAvecAide(vocabulaire, 'matiere', p.matiere)],
            ['Tube', valeurAvecAide(vocabulaire, 'recipient', p.recipient ?? null)],
            ['Préparation', valeurAvecAide(vocabulaire, 'preparation', p.preparation ?? null)],
          ]} />
          {estRq && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -18 }}>
              <Bouton taille="sm" onClick={() => setStabilites(true)}>Modifier les stabilités</Bouton>
            </div>
          )}
          <Rubrique titre="Stabilité (matière séparée)" lignes={[
            ['Ambiante', p.stabiliteAmbiante ?? null],
            ['Réfrigérée 2–8 °C', p.stabiliteRefrigeree ?? null],
            ['Congelée', p.stabiliteCongelee ?? null],
            ['Source', p.stabiliteSource ?? null],
            ['Endossement', (p.stabiliteAmbiante || p.stabiliteRefrigeree || p.stabiliteCongelee)
              ? (p.stabiliteValidee ? 'endossé par la RQ' : 'à relire — posé depuis la fiche ou la littérature') : null],
          ]} />
          <Rubrique titre="Diffusion" lignes={[
            ['Forme', p.formeDiffusion ? (FORMES[p.formeDiffusion] || p.formeDiffusion) : null],
            ['Au catalogue public', p.diffuseAuCatalogue ? 'oui' : 'non'],
          ]} />
          {/* ⚠️ La revue est DÉCLARABLE ICI depuis le 30/08/2026. Elle ne
              l'était que sur l'écran « Paramètres & valeurs usuelles », qui a
              été fermé : le référentiel l'affichait sans savoir l'écrire, et
              fermer sans porter ce geste aurait rendu la revue périodique
              impossible — sur des bornes cliniques, dont la revue est
              précisément ce qu'un auditeur demande. */}
          {estRq && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -18 }}>
              <Bouton taille="sm" onClick={() => setRevue(true)}>Déclarer une revue</Bouton>
            </div>
          )}
          {/* ⚠️ CE BLOC LIT LE REGISTRE DES REVUES DEPUIS LE 30/08/2026.
              Il lisait des champs `revue*` portés par le paramètre — un second
              registre du même fait, qu'aucun des 330 paramètres ne remplissait.
              La RQ a revu la 17-hydroxyprogestérone au centre de revue et la
              fiche a continué d'afficher « jamais revue ».
              La DÉCISION est désormais rendue telle que le registre la nomme
              (« intervalles inchangés » / « à modifier ») : la traduction se
              fait au serveur, en un seul endroit. */}
          <Rubrique titre="Revue des valeurs usuelles" lignes={[
            /* ⚠️ « Jamais revue » n'est PAS « en retard », et les fondre
                cacherait justement ce qu'un auditeur cherche. */
            ['Dernière revue', p.revueLe
              ? <span>{p.revueLe}{(p.revueHistorique || 0) > 1
                ? <span style={{ color: 'var(--text-muted)' }}>{` · ${p.revueHistorique} revues au total`}</span> : null}</span>
              : <span style={{ color: 'var(--status-warning)' }}>jamais revue</span>],
            ['Par', p.revuePar],
            ['Décision', p.revueDecision],
            ['Commentaire', p.revueCommentaire || null],
            /* ⚠️ L'échéance est CALCULÉE depuis la périodicité de la discipline.
                Absente, elle ne veut pas dire « pas de revue prévue » mais
                « aucune périodicité n'est paramétrée pour cette discipline » —
                deux choses qu'un auditeur ne confond pas. */
            ['Prochaine revue', p.revueLe
              ? (p.revueProchaine
                ? <span style={p.revueEnRetard ? { color: 'var(--status-warning)', fontWeight: 600 } : undefined}>
                  {p.revueProchaine}{p.revueEnRetard ? ' — en retard' : ''}
                </span>
                : <span style={{ color: 'var(--text-muted)' }}>
                  aucune périodicité paramétrée pour cette discipline
                </span>)
              : null],
            /* Un tampon d'avant la fusion des deux registres : il se dit, pour
                qu'on ne le prenne pas pour une revue enregistrée au centre. */
            ['Source', p.revueSource === 'tampon'
              ? <span style={{ color: 'var(--text-muted)' }}>
                déclarée avant le 30/08/2026, hors registre des revues
              </span> : null],
          ]} />
        </div>
        <div>
          <Rubrique titre="Ce qui le mesure" lignes={[
            /* `key` : la valeur d'une ligne de rubrique est rendue dans un
               tableau, et React la réclame même pour un élément unique. */
            ['Techniques', (
              <TechniquesDuParametre key="tech" p={p} estRq={estRq} onEcrit={onEcrit}
                ouvrirTechnique={ouvrirTechnique} />
            )],
            ['Équipement', p.equipementCode],
            ['Composants', p.composants.length ? p.composants.map((c) => (
              <span key={c.documentId} style={chip} title={c.nom}>{c.code} {marqueVersion(c.version)}</span>
            )) : null],
          ]} />
          {/* Déclarer un paramètre calculé, sa formule et ses composants. */}
          <div style={{ marginBottom: 12 }}>
            <LiaisonsParametre parametre={p} composants={p.composants} estRq={estRq}
              edition={editionCalcul} setEdition={setEditionCalcul}
              onEcrit={() => { setEditionCalcul(false); onEcrit?.(); }} />
          </div>
          <Rubrique titre="Les examens qui le portent" lignes={[
            ['', p.examens.length ? p.examens.map((x) => (
              <button key={x.documentId} className="ged-btn" style={{ ...chip, cursor: 'pointer' }}
                title={`${x.libelle} — ouvrir l’examen`} onClick={() => ouvrirExamen(x.code)}>
                {x.code} {marqueVersion(x.version)}
              </button>
            )) : <span style={{ color: 'var(--text-muted)' }}>aucun — composante pure</span>],
          ]} />
          <ValeursUsuelles p={p} estRq={estRq} onEcrit={() => onEcrit?.()} unites={unites} />
          {/* ══════════════════════════════════════════════════════════════
              PORTÉES DE L'ÉCRAN FERMÉ LE 30/08/2026. C'étaient les deux seules
              rubriques qui n'existaient nulle part ailleurs : sans elles,
              fermer aurait supprimé le seul endroit où l'on voit qu'un
              paramètre est surveillé — et un paramètre qu'on croit non
              surveillé se cherche longtemps.
              ══════════════════════════════════════════════════════════════ */}
          <Rubrique titre="Contrôle interne (CQI)" lignes={[
            ['', p.cartes?.length ? p.cartes.map((c) => (
              <span key={c.documentId} style={{ ...chip, opacity: c.actif ? 1 : 0.55 }}
                title={`${c.equipementNom || c.equipement}${c.actif ? '' : ' — carte inactive'}`}>
                {c.equipement || '—'}{c.statut ? ` · ${c.statut}` : ''}{c.actif ? '' : ' · inactive'}
              </span>
            )) : (
              /* ⚠️ Un état vide est une AFFIRMATION : il dit que ce paramètre
                 n'est surveillé par AUCUNE carte, ce qui est une information —
                 pas un écran qui n'a rien trouvé. */
              <span style={{ color: 'var(--text-muted)' }}>aucune carte de contrôle ne suit ce paramètre</span>
            )],
          ]} />
          <Rubrique titre="Critères d’acceptation" lignes={[
            ['', p.criteres?.length ? p.criteres.map((c) => (
              <div key={c.documentId} style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                {/* ⚠️ La SOURCE est nommée, jamais « SFBC » écrit en dur : le
                    jour où une deuxième entre au référentiel, la mention serait
                    fausse sur la rubrique même qui justifie des bornes. */}
                <strong>{c.source || 'source non nommée'}</strong>
                {c.libelle ? ` — ${c.libelle}` : ''}
                {c.valeursUsuellesSource
                  ? <div style={{ color: 'var(--text-muted)' }}>
                      Valeurs publiées par la source : {c.valeursUsuellesSource}{c.unite ? ` ${c.unite}` : ''}
                    </div>
                  : null}
              </div>
            )) : (
              /* ⚠️ La distinction que l'écran fermé tenait déjà : ce que la
                 SOURCE publie n'est pas ce que le laboratoire ADOPTE. Les
                 bornes adoptées sont au-dessus, dans « Valeurs usuelles ». */
              <span style={{ color: 'var(--text-muted)' }}>aucun critère de performance publié pour ce paramètre</span>
            )],
          ]} />
          <Rubrique titre="Validation de méthode (DVM)" lignes={[
            ['', p.dvms.length ? p.dvms.map((d) => (
              <span key={d.ref} style={chip} title={d.equipementCode || ''}>{d.ref} {marqueVersion(d.version)}{d.statut ? ` · ${d.statut}` : ''}</span>
            )) : <span style={{ color: 'var(--text-muted)' }}>aucune DVM ne cite ce paramètre</span>],
          ]} />
        </div>
      </div>
    </>
  );
  if (enPanneau) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 16 }}><span style={{ fontFamily: 'var(--font-mono)' }}>{p.code}</span> — {p.nom}</strong>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{sousTitre}</span>
        </div>
        {corps}
      </div>
    );
  }
  return (
    <Modale titre={`${p.code} — ${p.nom}`} sousTitre={sousTitre} onClose={onFerme} largeur={880} sansSaisie>
      {corps}
    </Modale>
  );
}

/**
 * LE DOSSIER D'UN PARAMÈTRE LIÉ — ouvert EN PANNEAU depuis un examen (18/10/2026).
 *
 * Il y avait DEUX dossiers pour un même paramètre : celui de la liste des
 * paramètres (`PanneauParametre`, complet, modifiable) et une fiche en
 * lecture seule, rendue dans le panneau des fiches liées quand on clique un
 * paramètre depuis un examen. Deux présentations des mêmes faits, qui
 * finissaient par ne plus dire la même chose. Il n'en reste qu'un : le panneau
 * rend LE dossier.
 *
 * Il se charge lui-même depuis `manuel-ref/parametres` (la liste, avec son
 * vocabulaire et ses unités, comme l'écran des paramètres) et retrouve le
 * paramètre par son CODE ou son documentId — l'appelant n'a pas à résoudre
 * l'un pour obtenir l'autre. Ouvrir un examen ou une technique depuis ce
 * panneau NAVIGUE vers le référentiel (le panneau se referme : on change de page).
 */
export function DossierParametreLie({ reference, estRq }: { reference: string; estRq: boolean }) {
  const { go, select } = useGed();
  const [liste, setListe] = useState<{ parametres: Parametre[]; vocabulaire: ChampVoc[]; unites: UniteVoc[] } | null>(null);
  const [erreur, setErreur] = useState('');
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let vivant = true;
    void moteur('manuel-ref/parametres').then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message); return; }
      setListe({
        parametres: (r.data.parametres as Parametre[]) || [],
        vocabulaire: (r.data.vocabulaire as ChampVoc[]) || [],
        unites: (r.data.unites as UniteVoc[]) || [],
      });
    });
    return () => { vivant = false; };
  }, [version]);
  if (erreur) return <EmptyState titre="Paramètre indisponible" indice={erreur} action={<Bouton taille="sm" onClick={() => { setErreur(''); setVersion((v) => v + 1); }}>Réessayer</Bouton>} />;
  if (!liste) return <Skeleton rows={6} />;
  const p = liste.parametres.find((x) => x.code === reference || x.documentId === reference);
  if (!p) return <EmptyState titre={`Aucun paramètre « ${reference} »`} indice="Le code a pu changer, ou le paramètre a été retiré du référentiel." />;
  const ouvrirExamen = (code: string) => { go('manuel'); select('selectedRef', code); };
  const ouvrirTechnique = (code: string) => { go('manuel'); select('selectedRef', `technique:${code}`); };
  return (
    <PanneauParametre enPanneau p={p} onFerme={() => undefined}
      ouvrirExamen={ouvrirExamen} ouvrirTechnique={ouvrirTechnique}
      estRq={estRq} onEcrit={() => setVersion((v) => v + 1)}
      vocabulaire={liste.vocabulaire} unites={liste.unites} />
  );
}

/**
 * CRÉER un paramètre — le bouton manquait (RQ, 18/08/2026 : « il n'y a pas de
 * bouton pour ajouter un nouveau paramètre »). Ce n'était pas un choix : il
 * n'existait aucune route d'écriture, le référentiel ne se remplissait que par
 * script de reprise.
 *
 * Les RÈGLES DE CODIFICATION s'affichent en face du champ, comme la RQ l'avait
 * demandé le 17/08 — et le serveur les revérifie : la règle à l'écran aide,
 * elle ne protège pas.
 */
function FormParametre({ onFerme, onCree, examens, vocabulaire }: {
  onFerme: () => void; onCree: (code: string) => void;
  /* Chaque examen porte la liste ACTUELLE de ses paramètres : la route de
     rattachement a une sémantique de REMPLACEMENT (`set`), n'envoyer que le
     nouveau viderait la composition. Le défaut aurait été muet — l'examen
     s'afficherait simplement avec un seul paramètre. */
  examens: { documentId: string; code: string; libelle: string; analyteIds: string[] }[];
  /** Sert la liste FERMÉE des disciplines (05/09/2026) — la même que
   *  Paramétrage → Disciplines, jamais une seconde saisie. */
  vocabulaire: ChampVoc[];
}) {
  const [v, setV] = useState({
    code: '', nom: '', discipline: '', matiere: '', uniteDefaut: '',
    /* LE NOMBRE DE DÉCIMALES, demandé DÈS la création (RQ, 24/08/2026). Vide =
       la règle générale décide. C'est la convention du mesurande, et elle se
       connaît au moment où on le crée : une testostérone en ng/dL se rend sans
       décimale, un cortisol en µg/dL en porte une. */
    decimales: '',
    typeResultat: 'quantitatif', formeDiffusion: 'composante',
  });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  /* La SUITE proposée une fois le paramètre créé (RQ, 19/08/2026 : « je
     voudrais que tu me suggères les deux créations en popup selon ce que je dis
     dans la liste déroulante »). L'écran ne décide pas à sa place : il propose
     ce que la forme de diffusion implique, et elle valide. */
  const [cree, setCree] = useState<string | null>(null);
  const [creeId, setCreeId] = useState<string>('');
  const [rattache, setRattache] = useState('');
  const [suite, setSuite] = useState('');
  const verdict = verifierCode(v.code);
  const ampute = motTronque(v.code);

  /* Ce que la forme de diffusion IMPLIQUE :
     · « analyse »    → il se commande seul, donc un examen de même code doit
                        exister — sinon personne ne peut le prescrire ;
     · « composante » → il se lit dans un examen composé, donc il faut le
                        rattacher à au moins un ;
     · « les deux »   → les deux à la fois. */
  const veutExamen = v.formeDiffusion === 'analyse' || v.formeDiffusion === 'lesdeux';
  const veutRattachement = v.formeDiffusion === 'composante' || v.formeDiffusion === 'lesdeux';

  return (
    <Modale titre="Nouveau paramètre" sousTitre="Ce que le laboratoire mesure." onClose={onFerme}>
      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <label style={{ fontSize: 12.5 }}>
            Code<br />
            <input value={v.code} autoFocus style={{ ...inputStyle, width: 190, fontFamily: 'var(--font-mono)' }}
              onChange={(e) => setV({ ...v, code: normaliserCode(e.currentTarget.value) })} />
            {v.code && !verdict.valide && <div style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 3 }}>{verdict.motif}</div>}
            {/* Un mot amputé se SIGNALE, il ne bloque pas : c'est un doute, pas
                une faute — « TSH » est un sigle, « ADENOVI » un mot coupé. */}
            {!!ampute.length && verdict.valide && (
              <div style={{ fontSize: 12.5, color: 'var(--amber-700)', marginTop: 3 }}>
                « {ampute.join(' », « ')} » ressemble à un mot coupé — préférez un sigle.
              </div>
            )}
          </label>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 380 }}>
            {REGLES_CODE.map((r) => <div key={r}>· {r}</div>)}
          </div>
        </div>
        <label style={{ fontSize: 12.5 }}>
          Nom<br />
          <input value={v.nom} onChange={(e) => setV({ ...v, nom: e.currentTarget.value })} style={{ ...inputStyle, width: '100%' }} />
        </label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5 }}>Discipline<br />
            {/* ⚠️ LISTE FERMÉE, PAS UN CHAMP LIBRE (05/09/2026) : la seule
                origine est Paramétrage → Disciplines, servie ici par
                `vocabulaire` — jamais une discipline inventée au fil d'une
                création de paramètre. */}
            <select value={v.discipline} onChange={(e) => setV({ ...v, discipline: e.currentTarget.value })} style={{ ...inputStyle, width: 170 }}>
              <option value="">— aucune —</option>
              {(vocabulaire.find((c) => c.cle === 'discipline')?.options || []).map((o) => (
                <option key={o.code} value={o.code}>{o.libelle}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12.5 }}>Matière<br />
            <input value={v.matiere} onChange={(e) => setV({ ...v, matiere: e.currentTarget.value })} style={{ ...inputStyle, width: 150 }} /></label>
          <label style={{ fontSize: 12.5 }}>Unité<br />
            <input value={v.uniteDefaut} onChange={(e) => setV({ ...v, uniteDefaut: e.currentTarget.value })} style={{ ...inputStyle, width: 110 }} /></label>
          {/* Les décimales n'ont de sens que sur une grandeur CHIFFRÉE : les
              demander sur un rendu qualitatif (« négatif ») serait un champ
              qui ne veut rien dire. */}
          {v.typeResultat !== 'qualitatif' && v.typeResultat !== 'texte' && (
            <label style={{ fontSize: 12.5 }}>Décimales<br />
              <input type="number" min={0} max={6} value={v.decimales} placeholder="auto"
                onChange={(e) => setV({ ...v, decimales: e.currentTarget.value })}
                style={{ ...inputStyle, width: 92 }} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, maxWidth: 200 }}>
                Comment le résultat s’écrit. Vide = trois chiffres significatifs.
              </div>
            </label>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5 }}>Type de résultat<br />
            <select value={v.typeResultat} onChange={(e) => setV({ ...v, typeResultat: e.currentTarget.value })} style={inputStyle}>
              <option value="quantitatif">Quantitatif</option><option value="qualitatif">Qualitatif</option>
              <option value="semiquantitatif">Semi-quantitatif</option><option value="texte">Texte</option>
            </select></label>
          <label style={{ fontSize: 12.5 }}>Forme de diffusion<br />
            <select value={v.formeDiffusion} onChange={(e) => setV({ ...v, formeDiffusion: e.currentTarget.value })} style={inputStyle}>
              <option value="composante">Composante — se lit dans l’analyse</option>
              <option value="analyse">Analyse — commandable seul</option>
              <option value="lesdeux">Les deux</option>
            </select></label>
        </div>
        {erreur && <div style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
        {suite && <div style={{ color: 'var(--green-600)', fontSize: 12.5 }}>{suite}</div>}

        {!cree ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Bouton variante="primaire" disabled={envoi || !verdict.valide || !v.nom.trim()} onClick={() => {
                setEnvoi(true); setErreur('');
                /* On n'envoie PAS la chaîne vide : le moteur attend un entier
                   ou rien, et `''` s'y lirait comme un zéro — « 0 décimale »
                   n'est pas « pas de convention ». */
                const corps = { ...v, decimales: v.decimales.trim() === '' ? null : Number(v.decimales) };
                void moteur('manuel-ref/parametres', corps).then((r) => {
                  setEnvoi(false);
                  if (!r.ok) { setErreur(r.message); return; }
                  setCree(v.code);
                  setCreeId(String((r.data as { documentId?: string }).documentId || ''));
                });
              }}>Créer le paramètre</Bouton>
            {/* Ce qui suivra est annoncé AVANT, pas découvert après : on ne
                surprend pas quelqu'un avec une seconde création. */}
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {veutExamen && veutRattachement
                ? 'Ensuite : créer l’examen de même code, et le rattacher à un examen composé.'
                : veutExamen ? 'Ensuite : créer l’examen de même code — sans lui, il ne se prescrit pas.'
                  : 'Ensuite : le rattacher à l’examen qui le contient.'}
            </span>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10, padding: 10, border: '1px solid var(--cyan-200)', background: 'var(--cyan-50)', borderRadius: 'var(--radius-sm)' }}>
            <strong style={{ fontSize: 12.5, color: 'var(--cyan-700)' }}>
              {cree} créé. {veutExamen || veutRattachement ? 'Ce qu’il reste à faire :' : 'Rien d’autre à faire.'}
            </strong>

            {veutExamen && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, flex: '1 1 260px' }}>
                  Il se commande seul : créer l’examen <strong>{cree}</strong> « {v.nom} » ?
                </span>
                <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => {
                    setEnvoi(true); setErreur('');
                    void moteur('manuel-ref/fiches', {
                      code: cree, libelle: v.nom, famille: v.discipline, echantillon: v.matiere,
                    }).then((r) => {
                      setEnvoi(false);
                      if (!r.ok) { setErreur(r.message); return; }
                      // Le serveur relie d'office le paramètre de même code.
                      setSuite(`Examen ${cree} créé et relié au paramètre.`);
                    });
                  }}>Créer l’examen {cree}</Bouton>
              </div>
            )}

            {veutRattachement && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5 }}>Le rattacher à un examen composé :</span>
                <select value={rattache} onChange={(e) => setRattache(e.currentTarget.value)} style={{ fontSize: 12.5, maxWidth: 260 }}>
                  <option value="">choisir un examen…</option>
                  {examens.map((x) => <option key={x.documentId} value={x.documentId}>{x.code} — {x.libelle.slice(0, 40)}</option>)}
                </select>
                <Bouton taille="sm" disabled={envoi || !rattache} onClick={() => {
                    setEnvoi(true); setErreur('');
                    const cible = examens.find((x) => x.documentId === rattache);
                    // La composition COMPLÈTE : l'existant plus le nouveau.
                    const ids = [...new Set([...(cible?.analyteIds || []), creeId])].filter(Boolean);
                    void moteur(`manuel-ref/fiches/${rattache}/analytes`, { analyteIds: ids }).then((r) => {
                      setEnvoi(false);
                      if (!r.ok) { setErreur(r.message); return; }
                      setSuite(`${cree} rattaché à ${examens.find((x) => x.documentId === rattache)?.code}.`);
                      setRattache('');
                    });
                  }}>Rattacher</Bouton>
              </div>
            )}

            <div>
              <Bouton taille="sm" onClick={() => onCree(cree)}>Terminer</Bouton>
            </div>
          </div>
        )}
      </div>
    </Modale>
  );
}

export function OngletParametres({ filtre, ouvert, setOuvert, parametres, erreur, onReessayer, ouvrirExamen, ouvrirTechnique, estRq, onCree, examens = [], creation, setCreation, vocabulaire = [], unites = [], criteresOrphelins = [] }: {
  filtre: string; ouvert: string | null; setOuvert: (c: string | null) => void;
  parametres: Parametre[] | null;
  /** L3 : l'échec de lecture de la liste, et la reprise — rendus par le tableau. */
  erreur?: string; onReessayer?: () => void;
  ouvrirExamen: (code: string) => void; ouvrirTechnique: (code: string) => void;
  estRq?: boolean; onCree?: () => void;
  /* La création est commandée depuis la barre d'outils du PARENT — le bouton
     « + » tient sa place en face de la barre de recherche, laquelle appartient
     à l'écran, pas à cette liste. */
  creation: boolean; setCreation: (v: boolean) => void;
  /* Les examens COMPOSABLES, pour le rattachement proposé après création. */
  examens?: { documentId: string; code: string; libelle: string; analyteIds: string[] }[];
  /* Les listes fermées et leurs « i », servis par le moteur. */
  vocabulaire?: ChampVoc[];
  unites?: UniteVoc[];
  /** Critères de performance qu'aucun code ne rattache à un paramètre du
   *  référentiel (05/09/2026) — voir `manuel-referentiel.ts`. */
  criteresOrphelins?: { libelle: string; source: string; motif: string }[];
}) {
  const visibles = useMemo(() => {
    const q = filtre.trim().toLowerCase();
    return (parametres || []).filter((p) => !p.archive
      /* La même règle que la liste des examens — elle regarde désormais les
         synonymes, les alias, et le LIBELLÉ des examens liés, pas seulement
         leur code (26/08/2026). */
      && correspond({ ...p, lies: p.examens }, q))
      .sort((a, b) => pertinence({ ...a, lies: a.examens }, q) - pertinence({ ...b, lies: b.examens }, q));
  }, [parametres, filtre]);

  const session = useGed((s) => s.session);
  const courant = ouvert ? visibles.find((p) => p.code === ouvert) || (parametres || []).find((p) => p.code === ouvert) : null;

  return (
    <div>
      {creation && (
        <FormParametre onFerme={() => setCreation(false)} examens={examens} vocabulaire={vocabulaire}
          onCree={(code) => { setCreation(false); setOuvert(code); onCree?.(); }} />
      )}
      {courant && (
        <PanneauParametre p={courant} onFerme={() => setOuvert(null)}
          ouvrirExamen={ouvrirExamen} ouvrirTechnique={ouvrirTechnique}
          estRq={!!estRq} onEcrit={() => onCree?.()} vocabulaire={vocabulaire} unites={unites} />
      )}
      {/* Un code faux fait disparaître un critère EN SILENCE — la fiche du
          paramètre se lit alors « aucun critère publié », qui est une
          affirmation fausse. Nommé, pour ne pas le taire (05/09/2026). */}
      {/* REPLIÉ par défaut (28/10/2026) : déplié, l'encadré repoussait la liste
          sous la ligne de flottaison à chaque recherche. Le compte reste en vue. */}
      {estRq && criteresOrphelins.length > 0 && (
        <details style={{ ...card, padding: '8px 14px', marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pastille compacte ton="warning">{criteresOrphelins.length}</Pastille>
            critère{criteresOrphelins.length > 1 ? 's' : ''} de performance sans paramètre rattaché — afficher
          </summary>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.6 }}>
            {criteresOrphelins.map((c, i) => (
              <div key={i}>{c.source ? `${c.source} — ` : ''}{c.libelle} : {c.motif}</div>
            ))}
          </div>
        </details>
      )}
      <div style={{ ...card, padding: 0, marginTop: 10 }}>
        <Tableau
          defilementHorizontal
          id="catalogue-parametres"
          ajusterLargeur
          /* L3 (10/10/2026) : niveau AVANCÉ — « Code » épinglé et obligatoire,
             filtres par colonne, 330 lignes virtualisées, export de référence,
             états intégrés (squelette tant que la liste n'est pas lue, erreur
             + Réessayer, vide). */
          niveau="avance"
          chargement={!parametres && !erreur}
          erreur={erreur || null}
          onReessayer={onReessayer}
          vide={{ titre: 'Aucun paramètre ne répond au filtre' }}
          export={{
            registre: 'catalogue-parametres', libelleRegistre: 'Catalogue des paramètres', auteur: session.name || session.key,
            filtres: filtre.trim() ? [`Recherche « ${filtre.trim()} »`] : [],
          }}
          lignes={visibles}
          cleLigne={(p) => p.documentId}
          ligneProps={(p) => ({
            onClick: () => setOuvert(ouvert === p.code ? null : p.code),
            style: {
              cursor: 'pointer',
              background: ouvert === p.code ? 'var(--surface-sunken)' : undefined,
            },
          })}
          colonnes={[
            {
              cle: 'code', libelle: 'Code', valeur: (p) => p.code, epinglee: true, obligatoire: true, export: true,
              style: { fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' },
              rendu: (p) => <strong>{p.code}</strong>,
            },
            { cle: 'nom', libelle: 'Paramètre', valeur: (p) => p.nom, export: true, rendu: (p) => p.nom },
            {
              // Le NUMÉRO de version, pas sa marque : « v10 » se rangerait
              // avant « v9 » si on triait le texte affiché.
              cle: 'version', libelle: 'v', titre: 'Version', valeur: (p) => p.version ?? 0, type: 'nombre',
              export: true, exporter: (p) => marqueVersion(p.version),
              style: { fontVariantNumeric: 'tabular-nums' },
              rendu: (p) => marqueVersion(p.version),
            },
            { cle: 'discipline', libelle: 'Discipline', valeur: (p) => p.discipline || '', type: 'liste', export: true, rendu: (p) => p.discipline || '—' },
            { cle: 'unite', libelle: 'Unité', valeur: (p) => p.uniteDefaut || '', type: 'liste', export: true, rendu: (p) => p.uniteDefaut || '—' },
            {
              cle: 'diffusion', libelle: 'Diffusion', valeur: (p) => p.formeDiffusion || '', type: 'liste',
              libelleValeur: (v) => (v === 'lesdeux' ? 'seul + composé' : v === 'analyse' ? 'seul' : 'composante'),
              export: true, exporter: (p) => (p.formeDiffusion === 'lesdeux' ? 'seul + composé' : p.formeDiffusion === 'analyse' ? 'seul' : 'composante'),
              rendu: (p) => (p.formeDiffusion === 'lesdeux' ? 'seul + composé' : p.formeDiffusion === 'analyse' ? 'seul' : 'composante'),
            },
            {
              cle: 'examens', libelle: 'Examens porteurs', filtrable: false, export: true, exporter: (p) => p.examens.map((x) => x.code).join(' '),
              // Le NOMBRE d'examens porteurs : un paramètre qui n'est porté par
              // aucun examen remonte en tête d'un tri croissant — c'est
              // exactement ce qu'on cherche.
              valeur: (p) => p.examens.length,
              rendu: (p) => (
                <>
                  {p.examens.length
                    ? p.examens.slice(0, 4).map((x) => <span key={x.documentId} style={chip}>{x.code}</span>)
                    : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  {p.examens.length > 4 && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>+{p.examens.length - 4}</span>}
                </>
              ),
            },
            { cle: 'dvm', libelle: 'DVM', valeur: (p) => p.dvms.length, type: 'nombre', export: true, rendu: (p) => p.dvms.length || '—' },
          ]}
        />
      </div>
    </div>
  );
}
